// ============================================================================
// OMNIPASS FRONTEND - API CLIENT
// Complete API integration layer sa enhanced error handling
// ============================================================================

import axios, { AxiosInstance, AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { API_BASE_URL } from '../config/api';
import { logger } from '../utils/logger';
import {
  AuditLogFilters,
  AuditLogListResponse,
  AuditLogSummaryResponse,
  AuditLogStats,
  AuthRequest,
  AuthResponse,
  UserProfile,
  CouponRequest,
  NotificationRequest,
  PassResponse,
  Setup2FARequest,
  Setup2FAResponse as Enhanced2FASetupResponse,
  Verify2FASetupRequest,
  Verify2FARequest as Enhanced2FAVerifyRequest,
  Verify2FAResponse as Enhanced2FAVerifyResponse,
  Disable2FARequest,
  RegenerateBackupCodesRequest,
  RegenerateBackupCodesResponse,
  User2FAStatusResponse
} from '../types/api';

// ============================================================================
// API RESPONSE INTERFACES
// ============================================================================

interface ErrorResponse {
  detail?: string;
  message?: string;
  [key: string]: unknown;
}

// ============================================================================
// ENHANCED ERROR HANDLING
// ============================================================================

export interface ApiError {
  message: string;
  status?: number;
  code?: string;
  details?: unknown;
  retryable?: boolean;
}

export class ApiErrorHandler {
  static createError(error: AxiosError): ApiError {
    const response = error.response;
    const status = response?.status;
    const errorData = response?.data as ErrorResponse;

    // Network errors
    if (error.code === 'NETWORK_ERROR' || !response) {
      return {
        message: 'Nema internetske veze. Proverite konekciju.',
        status: 0,
        code: 'NETWORK_ERROR',
        retryable: true
      };
    }

    // Timeout errors
    if (error.code === 'ECONNABORTED') {
      return {
        message: 'Zahtev je istekao. Pokušajte ponovo.',
        status: 0,
        code: 'TIMEOUT',
        retryable: true
      };
    }

    // HTTP status based errors
    switch (status) {
      case 400:
        return {
          message: errorData?.detail || 'Neispravni podaci. Proverite unos.',
          status,
          code: 'BAD_REQUEST',
          details: response.data,
          retryable: false
        };
      case 401:
        return {
          message: 'Niste autentifikovani. Molimo ulogujte se ponovo.',
          status,
          code: 'UNAUTHORIZED',
          retryable: false
        };
      case 403:
        return {
          message: 'Nemate dozvolu za ovu akciju.',
          status,
          code: 'FORBIDDEN',
          retryable: false
        };
      case 404:
        return {
          message: 'Traženi resurs nije pronađen.',
          status,
          code: 'NOT_FOUND',
          retryable: false
        };
      case 409:
        return {
          message: errorData?.detail || 'Podaci se već koriste.',
          status,
          code: 'CONFLICT',
          details: response.data,
          retryable: false
        };
      case 422:
        return {
          message: 'Podaci nisu validni.',
          status,
          code: 'VALIDATION_ERROR',
          details: response.data,
          retryable: false
        };
      case 429:
        return {
          message: 'Previše zahteva. Sačekajte malo pa pokušajte ponovo.',
          status,
          code: 'RATE_LIMIT',
          retryable: true
        };
      case 500:
        return {
          message: 'Greška servera. Pokušajte ponovo.',
          status,
          code: 'SERVER_ERROR',
          retryable: true
        };
      case 502:
      case 503:
      case 504:
        return {
          message: 'Server trenutno nije dostupan. Pokušajte ponovo.',
          status,
          code: 'SERVICE_UNAVAILABLE',
          retryable: true
        };
      default:
        return {
          message: errorData?.detail || 'Dogodila se neočekivana greška.',
          status,
          code: 'UNKNOWN_ERROR',
          details: response.data,
          retryable: false
        };
    }
  }

  static shouldShowNotification(error: ApiError): boolean {
    // Don't show notifications for certain error codes
    const silentCodes = ['UNAUTHORIZED']; // Handle auth errors differently
    return !silentCodes.includes(error.code || '');
  }
}

// Enhanced error logging
const logApiError = (error: AxiosError, context: string) => {
  const apiError = ApiErrorHandler.createError(error);

  logger.group(`🚨 API Error - ${context}`);
  logger.error('Message:', apiError.message);
  logger.error('Status:', apiError.status);
  logger.error('Code:', apiError.code);
  logger.error('Retryable:', apiError.retryable);
  if (apiError.details) {
    logger.error('Details:', apiError.details);
  }
  logger.error('Request URL:', error.config?.url);
  logger.error('Request Method:', error.config?.method);
  logger.groupEnd();
};

// ============================================================================
// CONFIGURATION
// ============================================================================

// Create main axios instance
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,  // Increased timeout for PassKit gRPC calls
  withCredentials: true,  // Send cookies with every request
  headers: {
    'Content-Type': 'application/json',
  },
});

// ============================================================================
// IN-MEMORY TOKEN STORE & CSRF
// ============================================================================

// Access token — in-memory only, never written to any Web Storage
const _tokenStore = { accessToken: null as string | null };
export const setAccessToken = (token: string | null): void => { _tokenStore.accessToken = token; };
export const getAccessToken = (): string | null => _tokenStore.accessToken;

// Refresh token — in-memory only. Does NOT survive a page reload by design:
// security > convenience. MSAL-authenticated sessions use MSAL's own silent
// token acquisition and never touch this store.
const _refreshStore = { refreshToken: null as string | null };
export const setRefreshToken = (token: string | null): void => { _refreshStore.refreshToken = token; };
export const getRefreshToken = (): string | null => _refreshStore.refreshToken;

/**
 * Perform a silent token refresh using the in-memory refresh token.
 * Returns the new access token, or null if no refresh token is available.
 */
export const silentRefresh = async (): Promise<string | null> => {
  try {
    const storedRefreshToken = getRefreshToken();
    if (!storedRefreshToken) return null;

    const res = await axios.post(`${API_BASE_URL}/auth/refresh`, { refresh_token: storedRefreshToken }, {
      withCredentials: true,
    });
    const token: string = res.data.access_token;
    setAccessToken(token);
    // Update refresh token if the server rotated it
    if (res.data.refresh_token) {
      setRefreshToken(res.data.refresh_token);
    }
    return token;
  } catch {
    setRefreshToken(null);
    return null;
  }
};

// ============================================================================
// REQUEST/RESPONSE INTERCEPTORS
// ============================================================================

// Request interceptor for auth token
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Handle 401 errors (token refresh)
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const newToken = await silentRefresh();
      if (newToken) {
        // Retry original request with refreshed token
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      }
      // Refresh failed – clear in-memory tokens, OAuth state, and redirect to login
      setAccessToken(null);
      setRefreshToken(null);
      sessionStorage.removeItem('oauth_state');
      sessionStorage.removeItem('pkce_verifier');
      globalThis.location.href = '/login';
      throw error;
    }

    // On network errors or server errors with no valid token, clear dangling OAuth state
    if (!error.response || (error.response.status >= 500 && !getAccessToken())) {
      sessionStorage.removeItem('oauth_state');
      sessionStorage.removeItem('pkce_verifier');
    }

    throw error;
  }
);

// ============================================================================
// ENHANCED API CLIENT WITH RETRY LOGIC
// ============================================================================

export class ApiClient {
  private static retryCount = 0;
  private static readonly maxRetries = 3;

  static async request<T>(
    apiCall: () => Promise<AxiosResponse<T>>,
    context: string,
    options: {
      showNotification?: boolean;
      retryable?: boolean;
      silent?: boolean;
    } = {}
  ): Promise<T> {
    const { showNotification = true, retryable = true, silent = false } = options;

    try {
      const response = await apiCall();
      this.retryCount = 0; // Reset on success
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const apiError = ApiErrorHandler.createError(axiosError);

      if (!silent) {
        logApiError(axiosError, context);
      }

      // Show notification if enabled
      if (showNotification && ApiErrorHandler.shouldShowNotification(apiError)) {
        // Import and use notification context here
        // This would be handled by the component using the API
      }

      // Retry logic for retryable errors
      if (retryable && apiError.retryable && this.retryCount < this.maxRetries) {
        this.retryCount++;
        logger.log(`🔄 Retrying request (${this.retryCount}/${this.maxRetries})...`);

        // Exponential backoff
        const delay = Math.pow(2, this.retryCount) * 1000;
        await new Promise(resolve => setTimeout(resolve, delay));

        return this.request(apiCall, context, options);
      }

      throw apiError;
    }
  }
}

// ============================================================================
// TYPES
// ============================================================================

// AuthRequest, AuthResponse, UserProfile, CouponRequest, NotificationRequest,
// PassResponse are canonical in types/api.ts — imported and re-exported below.
export type { AuthRequest, AuthResponse, UserProfile, CouponRequest, NotificationRequest, PassResponse } from '../types/api';

export interface PassCardRequest {
  title: string;
  description?: string;
  organization: string;
  member_id?: string;
  valid_until?: string;
}

export interface EventRequest {
  title: string;
  description?: string;
  location: string;
  event_date: string;
  event_time: string;
}

// 2FA Interfaces
export interface Setup2FAResponse {
  qr_code: string;
  secret: string;
  backup_codes: string[];
}

export interface Verify2FARequest {
  totp_code: string;
}

export interface Verify2FAResponse {
  verified: boolean;
  message: string;
}

export interface Enable2FARequest {
  secret: string;
  totp_code: string;
  backup_codes: string[];
}

export interface Enable2FAResponse {
  enabled: boolean;
  message: string;
}

export interface AnalyticsOverview {
  // Backend fields (official PassKit analytics)
  totalPasses: number;
  activePasses: number;
  redemptions: number;
  installRate: number;
  redemptionRate: number;
  recentActivity: Array<{
    date: string;
    installs: number;
    redemptions: number;
  }>;

  // Frontend compatibility fields (based on official PassKit data)
  total_passes: number;
  active_passes: number;
  total_downloads: number;
  apple_wallet: number;
  google_wallet: number;
  invalidated_passes?: number;
  chart_data?: Array<{
    date: string;
    passes?: number;
    created?: number;
    installed?: number;
    installs?: number;
    invalidated?: number;
    active?: number;
  }>;
  mom_change?: {
    total_passes?: number;
    installed_passes?: number;
  };
  recent_activity: Array<{
    date: string;
    downloads: number;
  }>;
}

export interface PassAnalytics {
  pass_id: string;
  total_downloads: number;
  active_installations: number;
  apple_wallet: number;
  google_wallet: number;
  usage_stats: {
    scans: number;
    updates: number;
    notifications_sent: number;
  };
  geographic_data: {
    [country: string]: number;
  };
}

// ============ AUTH API ============
export const authAPI = {
  login: async (credentials: AuthRequest): Promise<AuthResponse> => {
    logger.logAuth('[Frontend] Sending login request:', credentials);
    logger.log('[Frontend] API URL:', `${API_BASE_URL}/auth/login`);

    try {
      const response = await api.post('/auth/login', credentials);
      logger.log('[Frontend] Login response:', response);
      logger.log('[Frontend] Response data:', response.data);
      return response.data;
    } catch (error) {
      logger.error('[Frontend] Login error:', error);
      throw error;
    }
  },

  getMe: async (): Promise<UserProfile> => {
    try {
      const response = await api.get('/users/me');
      return response.data;
    } catch (error) {
      logger.error('[Frontend] Get me error:', error);
      throw error;
    }
  },

  getUserStatus: async (): Promise<UserProfile> => {
    try {
      const response = await api.get('/users/me');
      return response.data;
    } catch (error) {
      logger.error('[Frontend] Get user status error:', error);
      throw error;
    }
  },

  logout: async (): Promise<void> => {
    await api.post('/auth/logout');
    setAccessToken(null);
    setRefreshToken(null);
    localStorage.removeItem('userRole');
  },

  // 2FA Functions
  setup2FA: async (): Promise<Setup2FAResponse> => {
    logger.log('[2FA API] Setting up 2FA...');
    const response = await api.post('/two-factor/setup');
    logger.log('[2FA API] Setup response:', response.data);
    return response.data;
  },

  verify2FA: async (totpCode: string): Promise<Verify2FAResponse> => {
    logger.log('[2FA API] Verifying code...');
    const response = await api.post('/two-factor/verify', {
      totp_code: totpCode,
      backup_code: totpCode  // Try as both TOTP and backup code
    });
    logger.log('[2FA API] Verification response:', response.data);
    return response.data;
  },

  disable2FA: async (): Promise<{ message: string }> => {
    const response = await api.post('/two-factor/disable');
    return response.data;
  },

  enableExisting2FA: async (): Promise<{ message: string }> => {
    const response = await api.post('/two-factor/enable-existing');
    return response.data;
  },

  reset2FA: async (): Promise<{ message: string }> => {
    logger.log('[2FA API] Completely resetting 2FA...');
    const response = await api.post('/two-factor/reset');
    logger.log('[2FA API] Reset response:', response.data);
    return response.data;
  },

  enable2FA: async (secret: string, totpCode: string, backupCodes: string[]): Promise<Enable2FAResponse> => {
    logger.log('[2FA API] Enabling 2FA...');
    const response = await api.post('/two-factor/enable', {
      secret: secret,
      totp_code: totpCode,
      backup_codes: backupCodes
    });
    logger.log('[2FA API] Enable response:', response.data);
    return response.data;
  },

  verifyBackupCode: async (backupCode: string): Promise<{
    verified: boolean;
    message: string;
    remaining_codes?: number;
  }> => {
    logger.log('[2FA API] Verifying backup code...');
    const response = await api.post('/two-factor/verify-backup', {
      backup_code: backupCode
    });
    logger.log('[2FA API] Backup verification response:', response.data);
    return response.data;
  },

  getBackupCodesStatus: async (): Promise<{
    total_codes: number;
    unused_codes: number;
    used_codes: number;
    expired_codes: number;
    status: string;
    expiry_days?: number;
  }> => {
    logger.log('[2FA API] Getting backup codes status...');
    const response = await api.get('/two-factor/backup-status');
    logger.log('[2FA API] Backup status response:', response.data);
    return response.data;
  },

  regenerateBackupCodes: async (): Promise<Setup2FAResponse> => {
    logger.log('[2FA API] Regenerating backup codes...');
    const response = await api.post('/two-factor/regenerate-backup');
    logger.log('[2FA API] Regenerate response:', response.data);
    return response.data;
  },

  verifyTwoFactorLogin: async (data: {
    user_id: string;
    totp_code?: string;
    backup_code?: string;
  }): Promise<AuthResponse> => {
    logger.log('[2FA API] Verifying two-factor login...');
    const response = await api.post('/auth/verify-login', data);
    logger.log('[2FA API] Two-factor login response:', response.data);
    return response.data;
  },

  // Password change
  changePassword: async (data: {
    current_password: string;
    new_password: string;
  }): Promise<{ message: string }> => {
    logger.log('[Auth API] Changing password...');
    const response = await api.patch('/users/me/password', data);
    logger.log('[Auth API] Password change response:', response.data);
    return response.data;
  },

  // Legal acceptance
  acceptLegalTerms: async (): Promise<{ message: string; timestamp: string }> => {
    logger.log('[Auth API] Accepting legal terms...');
    const response = await api.post('/auth/accept-terms');
    logger.log('[Auth API] Terms acceptance response:', response.data);
    return response.data;
  }
};

// ============ MEMBERSHIP CARDS API ============
export const membershipAPI = {
  // Template management
  getTemplates: async () => {
    const response = await api.get('/membership/templates');
    return response.data;
  },

  createTemplate: async (templateData: Record<string, unknown>) => {
    const response = await api.post('/membership/templates', templateData);
    return response.data;
  },

  // Pass management for specific template
  getPasses: async (templateId: string) => {
    const response = await api.get(`/membership/templates/${templateId}/passes`);
    return response.data;
  },

  createPass: async (templateId: string, passData: PassCardRequest): Promise<PassResponse> => {
    const response = await api.post(`/membership/templates/${templateId}/passes`, passData);
    return response.data;
  },

  // Analytics
  getAnalytics: async () => {
    const response = await api.get('/membership/analytics');
    return response.data;
  },

  // Legacy methods for compatibility
  create: async (cardData: PassCardRequest): Promise<PassResponse> => {
    // For now, create with default template or throw error
    logger.warn('membershipAPI.create is deprecated. Use createPass with templateId instead.');
    throw new Error('Template ID required. Use createPass method instead.');
  },

  list: async () => {
    // Return templates instead of passes
    logger.warn('membershipAPI.list is deprecated. Use getTemplates instead.');
    return await membershipAPI.getTemplates();
  },
};

// ============ COUPONS API ============
export const couponsAPI = {
  create: async (couponData: CouponRequest): Promise<PassResponse> => {
    const response = await api.post('/passes/coupon', couponData);
    return response.data;
  },

  update: async (passId: string, couponData: CouponRequest): Promise<PassResponse> => {
    const response = await api.put(`/passes/coupon/${passId}`, couponData);
    return response.data;
  },

  delete: async (passId: string): Promise<{ message: string }> => {
    const response = await api.delete(`/passes/coupon/${passId}`);
    return response.data;
  },

  list: async () => {
    const response = await api.get('/passes/coupon');
    return response.data;
  },
};

// ============ EVENTS API ============
export const eventsAPI = {
  create: async (eventData: EventRequest): Promise<PassResponse> => {
    const response = await api.post('/passes/event', eventData);
    return response.data;
  },

  update: async (passId: string, eventData: EventRequest): Promise<PassResponse> => {
    const response = await api.put(`/passes/event/${passId}`, eventData);
    return response.data;
  },

  delete: async (passId: string): Promise<{ message: string }> => {
    const response = await api.delete(`/passes/event/${passId}`);
    return response.data;
  },

  deleteEntireEvent: async (eventId: string): Promise<{ message: string }> => {
    const response = await api.delete(`/events/${eventId}`);
    return response.data;
  },

  list: async () => {
    const response = await api.get('/passes/event');
    return response.data;
  },
};

// ============ EVENT TICKETS MANAGEMENT API ============
export const eventTicketsAPI = {
  // Events management
  events: {
    list: async () => {
      const response = await api.get('/events/events');
      return response.data;
    },

    create: async (eventData: Record<string, unknown>) => {
      const response = await api.post('/events/events', eventData);
      return response.data;
    },

    update: async (eventId: string, eventData: Record<string, unknown>) => {
      const response = await api.put(`/events/events/${eventId}`, eventData);
      return response.data;
    },

    delete: async (eventId: string) => {
      const response = await api.delete(`/events/events/${eventId}`);
      return response.data;
    }
  },

  // Venues management
  venues: {
    list: async () => {
      const response = await api.get('/events/venues');
      return response.data;
    },

    create: async (venueData: Record<string, unknown>) => {
      const response = await api.post('/events/venues', venueData);
      return response.data;
    },

    update: async (venueId: string, venueData: Record<string, unknown>) => {
      const response = await api.put(`/events/venues/${venueId}`, venueData);
      return response.data;
    },

    delete: async (venueId: string) => {
      const response = await api.delete(`/events/venues/${venueId}`);
      return response.data;
    }
  },

  // Productions management
  productions: {
    list: async () => {
      const response = await api.get('/events/productions');
      return response.data;
    },

    create: async (productionData: Record<string, unknown>) => {
      const response = await api.post('/events/productions', productionData);
      return response.data;
    },

    update: async (productionId: string, productionData: Record<string, unknown>) => {
      const response = await api.patch(`/events/productions/${productionId}`, productionData);
      return response.data;
    },

    delete: async (productionId: string) => {
      const response = await api.delete(`/events/productions/${productionId}`);
      return response.data;
    }
  },

  // Issue a new ticket
  issueTicket: async (ticketData: Record<string, unknown>) => {
    try {
      const response = await api.post('/productions/tickets', ticketData);
      return response.data;
    } catch (error) {
      logApiError(error as AxiosError, 'Events - Issue Ticket');
      throw error;
    }
  }
};

// ============ NOTIFICATIONS API ============
export const notificationsAPI = {
  send: async (notificationData: NotificationRequest) => {
    const response = await api.post('/notifications/send', notificationData);
    return response.data;
  },
};

// ============ ANALYTICS API ============
export const analyticsAPI = {
  getOverview: async (options?: {
    period?: 'DAY' | 'MONTH' | 'YEAR';
    start_date?: string;
    end_date?: string;
  }): Promise<AnalyticsOverview> => {
    // Build query parameters
    const params = new URLSearchParams();
    if (options?.period) {
      params.append('period', options.period);
    }
    if (options?.start_date) {
      params.append('start_date', options.start_date);
    }
    if (options?.end_date) {
      params.append('end_date', options.end_date);
    }

    const queryString = params.toString();
    const endpoint = queryString ? `/analytics/overview?${queryString}` : '/analytics/overview';

    const response = await api.get(endpoint);

    // Backend returns: total_templates, total_passes, total_redemptions, active_passes, redemption_rate, creation_rate, time_period
    const backendData = response.data;

    if (backendData && typeof backendData.total_passes === 'number') {
      // Create adapter that maps backend format to frontend format
      const adaptedData: AnalyticsOverview = {
        // Map backend fields to frontend camelCase fields
        totalPasses: backendData.total_passes || 0,
        activePasses: backendData.active_passes || 0,
        redemptions: backendData.total_redemptions || 0,
        installRate: ((backendData.active_passes || 0) / (backendData.total_passes || 1)) * 100, // Calculate install rate: active/total
        redemptionRate: backendData.redemption_rate || 0,
        recentActivity: backendData.recent_activity || [], // Now comes from Mock API

        // Frontend compatibility fields (use real data from Mock API)
        total_passes: backendData.total_passes || 0,
        active_passes: backendData.active_passes || 0,
        total_downloads: backendData.total_passes || 0, // Use total_passes as downloads for now
        apple_wallet: backendData.apple_wallet ?? 0, // Real data from PassKit API
        google_wallet: backendData.google_wallet ?? 0, // Real data from PassKit API
        invalidated_passes: backendData.invalidated_passes || 0,
        chart_data: backendData.chart_data || [],
        mom_change: backendData.mom_change || null,
        recent_activity: (backendData.recent_activity || []).map((item: { date: string; downloads?: number; installs?: number }) => ({
          date: item.date,
          downloads: item.downloads || item.installs || 0
        })), // Transform to frontend format
      };

      return adaptedData;
    }

    throw new Error('Invalid analytics response format');
  },

  getPassAnalytics: async (passId: string): Promise<PassAnalytics> => {
    const response = await api.get(`/analytics/passes/${passId}`);
    return response.data;
  }
};

// ============ GENERAL PASSES API ============
export const passesAPI = {
  listAll: async (skip: number = 0, limit: number = 100) => {
    const response = await api.get(`/passes?skip=${skip}&limit=${limit}`);
    return response.data;
  },
};

export default api;

// ============ PASS INSTANCES API ============
export interface PassInstance {
  pass_id: string;
  template_id: string;
  template_name?: string;
  pass_data: Record<string, unknown>;
  recipient_name: string;
  recipient_email: string;
  status: 'active' | 'revoked' | 'expired';
  created_at: string;
  issued_at?: string;
  installation_url?: string;
  qr_code?: string;
  apple_wallet_url?: string;
  google_wallet_url?: string;
  analytics?: {
    total_installations: number;
    apple_installations: number;
    google_installations: number;
    last_scan?: string;
  };
}

export interface PassInstanceCreateRequest {
  template_id: string;
  pass_data: Record<string, unknown>;
  recipient_name: string;
  recipient_email: string;
  auto_issue?: boolean;
}

export interface PassInstanceListResponse {
  passes: PassInstance[]; // Backend returns 'passes' not 'data'
  total: number;
  page: number;
  per_page: number;
  filters_applied?: any;
}

export interface PassInstanceStats {
  total_passes: number;
  active_passes: number;
  revoked_passes: number;
  total_installations: number;
  apple_installations: number;
  google_installations: number;
  creation_rate_24h: number;
  installation_rate: number;
}

export const passInstancesAPI = {
  // List pass instances with filtering and pagination
  list: async (
    page: number = 1,
    per_page: number = 20,
    filters?: {
      template_id?: string;
      status?: string;
      search?: string;
      start_date?: string;
      end_date?: string;
    }
  ): Promise<PassInstanceListResponse> => {
    const params = new URLSearchParams({
      page: page.toString(),
      per_page: per_page.toString()
    });

    if (filters?.template_id) params.append('template_id', filters.template_id);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.search) params.append('search', filters.search);
    if (filters?.start_date) params.append('start_date', filters.start_date);
    if (filters?.end_date) params.append('end_date', filters.end_date);

    const response = await api.get(`/passes/?${params}`);
    return response.data;
  },

  // Get single pass instance
  get: async (passId: string): Promise<PassInstance> => {
    const response = await api.get(`/passes/${passId}`);
    return response.data;
  },

  // Create new pass instance
  create: async (passData: PassInstanceCreateRequest): Promise<PassInstance> => {
    const response = await api.post('/passes/', passData);
    return response.data;
  },

  // Update pass instance
  update: async (passId: string, passData: Partial<PassInstanceCreateRequest>): Promise<PassInstance> => {
    const response = await api.put(`/passes/${passId}`, passData);
    return response.data;
  },

  // Delete pass instance
  delete: async (passId: string): Promise<{ message: string }> => {
    const response = await api.delete(`/passes/${passId}`);
    return response.data;
  },

  // Issue pass (send to recipient)
  issue: async (passId: string): Promise<{
    message: string;
    installation_url: string;
    qr_code: string;
    apple_wallet_url?: string;
    google_wallet_url?: string;
  }> => {
    const response = await api.post(`/passes/${passId}/issue`);
    return response.data;
  },

  // Revoke pass
  revoke: async (passId: string, reason?: string): Promise<{ message: string }> => {
    const response = await api.post(`/passes/${passId}/revoke`, { reason });
    return response.data;
  },

  // Get installation URL and QR code
  getInstallationUrl: async (passId: string): Promise<{
    installation_url: string;
    qr_code: string;
    apple_wallet_url?: string;
    google_wallet_url?: string;
  }> => {
    const response = await api.get(`/passes/${passId}/install`);
    return response.data;
  },

  // Bulk operations
  bulkCreate: async (passes: PassInstanceCreateRequest[]): Promise<{
    created: PassInstance[];
    failed: Array<{ request: PassInstanceCreateRequest; error: string }>;
    summary: { total: number; created: number; failed: number };
  }> => {
    const response = await api.post('/passes/bulk/create', { passes });
    return response.data;
  },

  bulkIssue: async (passIds: string[]): Promise<{
    issued: Array<{ pass_id: string; installation_url: string; qr_code: string }>;
    failed: Array<{ pass_id: string; error: string }>;
    summary: { total: number; issued: number; failed: number };
  }> => {
    const response = await api.post('/passes/bulk/issue', { pass_ids: passIds });
    return response.data;
  },

  // Get statistics
  getStats: async (filters?: {
    template_id?: string;
    start_date?: string;
    end_date?: string;
  }): Promise<PassInstanceStats> => {
    const params = new URLSearchParams();
    if (filters?.template_id) params.append('template_id', filters.template_id);
    if (filters?.start_date) params.append('start_date', filters.start_date);
    if (filters?.end_date) params.append('end_date', filters.end_date);

    const queryString = params.toString();
    const endpoint = queryString ? `/passes/stats?${queryString}` : '/passes/stats';

    const response = await api.get(endpoint);
    return response.data;
  }
};

// ============ TEMPLATES API ============
export interface PassTemplate {
  id: string; // Changed from template_id to match backend
  company_id?: string;
  name: string;
  description?: string;
  pass_type: string;
  design_config?: Record<string, unknown>;
  fields_config?: Record<string, unknown>;
  is_active: boolean;
  project_id?: number;
  passkit_template_id?: string;
  created_at: string;
  updated_at?: string;
  total_passes_created?: string;
  active_passes_count?: string;
}

// Database template interface for template assignments
export interface DatabaseTemplate {
  template_id: string;
  name: string;
  description?: string;
  pass_type: string;
  is_active: boolean;
}

export interface PassTemplateResponse {
  template: PassTemplate;
}

export interface PassTemplateListResponse {
  templates: PassTemplate[];
  total: number;
  skip: number;
  limit: number;
}

export interface TemplateCreateRequest {
  name: string;
  description?: string;
  pass_type: string;
  project_id: number;
  design_config?: {
    background_color?: string;
    foreground_color?: string;
    logo_text?: string;
  };
  fields_config?: Record<string, unknown>;
}

export const templatesAPI = {
  list: async (page: number = 1, per_page: number = 50): Promise<{ templates: PassTemplate[]; total: number; page: number; per_page: number; total_pages: number }> => {
    const response = await api.get(`/templates/?page=${page}&per_page=${per_page}`);
    const body = response.data;
    // Canonical envelope: { data: [...], meta: {...} }
    return {
      templates: body.data ?? body.templates ?? body,
      total: body.meta?.total ?? body.total ?? 0,
      page: body.meta?.page ?? page,
      per_page: body.meta?.per_page ?? per_page,
      total_pages: body.meta?.total_pages ?? 1,
    };
  },

  get: async (templateId: string): Promise<PassTemplateResponse> => {
    const response = await api.get(`/templates/${templateId}`);
    return response.data;
  },

  create: async (templateData: TemplateCreateRequest): Promise<PassTemplateResponse> => {
    const response = await api.post('/templates/', templateData);
    return response.data;
  },

  update: async (templateId: string, templateData: Partial<TemplateCreateRequest>): Promise<PassTemplateResponse> => {
    const response = await api.put(`/templates/${templateId}`, templateData);
    return response.data;
  },

  delete: async (templateId: string): Promise<void> => {
    await api.delete(`/templates/${templateId}`);
  },

  publish: async (templateId: string): Promise<void> => {
    await api.post(`/templates/${templateId}/publish`);
  },

  getStats: async (templateId: string): Promise<Record<string, unknown>> => {
    const response = await api.get(`/templates/${templateId}/stats`);
    return response.data;
  },

  // Admin endpoints for template management
  adminListAll: async (page: number = 1, per_page: number = 50, company_id?: string): Promise<AdminTemplateListResponse> => {
    const params = new URLSearchParams({
      page: page.toString(),
      per_page: per_page.toString()
    });
    if (company_id) {
      params.append('company_id', company_id);
    }
    const response = await api.get(`/templates/admin?${params}`);
    return response.data;
  },

  adminCreate: async (templateData: AdminTemplateCreateRequest): Promise<{ message: string; template_id: string }> => {
    const response = await api.post('/templates/admin', templateData);
    return response.data;
  },

  adminUpdate: async (templateId: string, templateData: { name?: string; description?: string; company_id?: string; status?: string }): Promise<{ message: string }> => {
    const response = await api.patch(`/templates/admin/${templateId}`, templateData);
    return response.data;
  },

  adminDelete: async (templateId: string): Promise<{ message: string }> => {
    const response = await api.delete(`/templates/admin/${templateId}`);
    return response.data;
  },

  // Enhanced Templates API with new backend features

  // Advanced filtering with search, pagination, and status filters
  listWithFilters: async (
    page: number = 1,
    per_page: number = 50,
    filters?: {
      template_type?: string;
      is_active?: boolean;
      is_published?: boolean;
      search?: string;
    }
  ): Promise<PassTemplateListResponse> => {
    const params = new URLSearchParams({
      page: page.toString(),
      per_page: per_page.toString()
    });

    if (filters?.template_type) params.append('template_type', filters.template_type);
    if (filters?.is_active !== undefined) params.append('is_active', filters.is_active.toString());
    if (filters?.is_published !== undefined) params.append('is_published', filters.is_published.toString());
    if (filters?.search) params.append('search', filters.search);

    const response = await api.get(`/templates/?${params}`);
    return response.data;
  },

  // Bulk operations
  bulkActivate: async (templateIds: string[]): Promise<{
    message: string;
    activated: number;
    failed: Array<{ template_id: string; error: string }>;
  }> => {
    const response = await api.post('/templates/bulk/activate', templateIds);
    return response.data;
  },

  bulkDeactivate: async (templateIds: string[]): Promise<{
    message: string;
    deactivated: number;
    failed: Array<{ template_id: string; error: string }>;
  }> => {
    const response = await api.post('/templates/bulk/deactivate', templateIds);
    return response.data;
  },

  bulkDelete: async (templateIds: string[]): Promise<{
    message: string;
    deleted: number;
    failed: Array<{ template_id: string; error: string }>;
  }> => {
    const response = await api.post('/templates/bulk/delete', templateIds);
    return response.data;
  },

  // Template duplication
  duplicate: async (templateId: string, newName?: string): Promise<{
    message: string;
    template_id: string;
    duplicated_template: PassTemplate;
  }> => {
    const response = await api.post(`/templates/${templateId}/duplicate`, { new_name: newName });
    return response.data;
  },

  // Enhanced statistics
  getTemplateStats: async (): Promise<{
    total_templates: number;
    active_templates: number;
    published_templates: number;
    total_passes_created: number;
    templates_by_type: Array<{ pass_type: string; count: number }>;
    recent_activity: Array<{
      template_id: string;
      template_name: string;
      action: string;
      count: number;
      date: string;
    }>;
  }> => {
    const response = await api.get('/templates/stats');
    return response.data;
  },

  // Aggregate statistics for dashboard
  getAggregateStats: async (): Promise<{
    overview: {
      total_templates: number;
      active_templates: number;
      total_passes_created: number;
      active_passes: number;
      recent_passes_7_days: number;
    };
    template_type_distribution: Array<{
      type: string;
      count: number;
    }>;
    top_templates: Array<{
      template_id: string;
      name: string;
      pass_type: string;
      total_passes: number;
    }>;
    generated_at: string;
  }> => {
    const response = await api.get('/templates/aggregate/stats');
    return response.data;
  }
};

// Admin template management interfaces
export interface AdminTemplateResponse {
  id: string;
  name: string;
  description?: string;
  pass_type: string;
  company_id?: string;
  company_name?: string;
  passkit_template_id?: string;
  is_active: boolean;
  is_published: boolean;
  created_at?: string;
  updated_at?: string;
  total_passes_created: string;
  active_passes_count: string;
}

export interface AdminTemplateListResponse {
  templates: AdminTemplateResponse[];
  total_count: number;
  page: number;
  per_page: number;
  total_pages: number;
}

export interface AdminTemplateCreateRequest {
  passkit_template_id: string;
  name: string;
  company_id?: string;
  description?: string;
}

// ============================================================================
// COMPANIES API
// ============================================================================

export interface Company {
  id: number;
  company_id: string;
  company_name: string;
  display_name?: string;
  industry?: string;
  primary_email: string;
  phone?: string;
  website?: string;
}

export interface MyCompanyResponse {
  company_id: string;
  company_name: string;
  display_name?: string;
  industry?: string;
  company_size?: string;
  primary_email?: string;
  tier?: string;
  status?: string;
  phone?: string;
  website?: string;
}

export interface UserLimitStatus {
  current_users: number;
  max_users: number;
  can_add_user: boolean;
  tier: string;
  percentage_used: number;
}

export interface CompaniesListResponse {
  companies: Company[];
  total: number;
}

export const companiesAPI = {
  // Get all companies (admin only)
  getAllCompanies: async (): Promise<CompaniesListResponse> => {
    try {
      const response = await api.get('/companies/');
      return {
        companies: response.data.map((company: { company_id: string; company_name: string; display_name?: string; industry?: string; primary_email?: string; phone?: string; website?: string }) => ({
          id: company.company_id, // Use company_id as the main ID
          company_id: company.company_id,
          company_name: company.company_name,
          display_name: company.display_name,
          industry: company.industry,
          primary_email: company.primary_email,
          phone: company.phone,
          website: company.website
        })),
        total: response.data.length
      };
    } catch (error) {
      logger.error('Failed to fetch companies:', error);
      throw error;
    }
  },

  // Get current user's company
  getMyCompany: async (): Promise<MyCompanyResponse> => {
    const response = await api.get('/companies/me');
    return response.data;
  },

  // Get user limit status for current company
  getUserLimitStatus: async (): Promise<UserLimitStatus> => {
    const response = await api.get('/companies/me/user-limit-status');
    return response.data;
  }
};

// Export 2FA functions for convenience
export const {
  setup2FA,
  verify2FA,
  disable2FA,
  enableExisting2FA,
  reset2FA,
  enable2FA,
  verifyBackupCode,
  getBackupCodesStatus,
  regenerateBackupCodes,
  changePassword
} = authAPI;

// ============================================================================
// AUDIT LOGS API
// ============================================================================

export const auditLogsAPI = {
  // Get audit logs with filtering and pagination
  getLogs: async (
    page: number = 1,
    size: number = 20,
    filters?: AuditLogFilters
  ): Promise<AuditLogListResponse> => {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: size.toString()
      });

      // Add filters to query params
      if (filters?.start_date) params.append('start_date', filters.start_date);
      if (filters?.end_date) params.append('end_date', filters.end_date);
      if (filters?.action) params.append('action', filters.action);
      if (filters?.user_id) params.append('user_id', filters.user_id);
      if (filters?.user_email) params.append('user_email', filters.user_email);
      if (filters?.endpoint) params.append('endpoint', filters.endpoint);
      if (filters?.method) params.append('method', filters.method);
      if (filters?.status) params.append('status', filters.status.toString());
      if (filters?.ip_address) params.append('ip_address', filters.ip_address);
      if (filters?.success !== undefined) params.append('success', filters.success.toString());

      const queryString = params.toString();
      const response = await api.get(`/monitoring/audit/logs?${queryString}`);
      return response.data;
    } catch (error) {
      logApiError(error as AxiosError, 'Audit Logs - Get Logs');
      throw error;
    }
  },

  // Get audit log summary data
  getSummary: async (
    period?: 'day' | 'week' | 'month',
    start_date?: string,
    end_date?: string
  ): Promise<AuditLogSummaryResponse[]> => {
    try {
      const params = new URLSearchParams();
      if (period) params.append('period', period);
      if (start_date) params.append('start_date', start_date);
      if (end_date) params.append('end_date', end_date);

      const queryString = params.toString();
      const endpoint = queryString ? `/monitoring/audit/summary?${queryString}` : '/monitoring/audit/summary';

      const response = await api.get(endpoint);
      return response.data;
    } catch (error) {
      logApiError(error as AxiosError, 'Audit Logs - Get Summary');
      throw error;
    }
  },

  // Get audit statistics
  getStats: async (
    period?: 'day' | 'week' | 'month',
    start_date?: string,
    end_date?: string
  ): Promise<AuditLogStats> => {
    try {
      const params = new URLSearchParams();
      if (period) params.append('period', period);
      if (start_date) params.append('start_date', start_date);
      if (end_date) params.append('end_date', end_date);

      const queryString = params.toString();
      const endpoint = queryString ? `/monitoring/audit/stats?${queryString}` : '/monitoring/audit/stats';

      const response = await api.get(endpoint);
      return response.data;
    } catch (error) {
      logApiError(error as AxiosError, 'Audit Logs - Get Stats');
      throw error;
    }
  },

  // Export audit logs
  exportLogs: async (
    format: 'csv' | 'json' = 'csv',
    filters?: AuditLogFilters
  ): Promise<Blob> => {
    try {
      const params = new URLSearchParams({
        format: format
      });

      // Add filters to query params
      if (filters?.start_date) params.append('start_date', filters.start_date);
      if (filters?.end_date) params.append('end_date', filters.end_date);
      if (filters?.action) params.append('action', filters.action);
      if (filters?.user_id) params.append('user_id', filters.user_id);
      if (filters?.user_email) params.append('user_email', filters.user_email);
      if (filters?.endpoint) params.append('endpoint', filters.endpoint);
      if (filters?.method) params.append('method', filters.method);
      if (filters?.status) params.append('status', filters.status.toString());
      if (filters?.ip_address) params.append('ip_address', filters.ip_address);
      if (filters?.success !== undefined) params.append('success', filters.success.toString());

      const queryString = params.toString();
      const response = await api.get(`/monitoring/audit/export?${queryString}`, {
        responseType: 'blob'
      });

      return response.data;
    } catch (error) {
      logApiError(error as AxiosError, 'Audit Logs - Export');
      throw error;
    }
  }
};

// ============================================================================
// ENHANCED 2FA API
// ============================================================================

export const enhanced2FAAPI = {
  // Setup 2FA (get QR code and backup codes)
  setup: async (request: Setup2FARequest): Promise<Enhanced2FASetupResponse> => {
    try {
      const response = await api.post('/auth/2fa/setup', request);
      return response.data;
    } catch (error) {
      logApiError(error as AxiosError, 'Enhanced 2FA - Setup');
      throw error;
    }
  },

  // Verify 2FA setup (complete setup process)
  verifySetup: async (request: Verify2FASetupRequest): Promise<{ message: string }> => {
    try {
      const response = await api.post('/auth/2fa/setup/verify', request);
      return response.data;
    } catch (error) {
      logApiError(error as AxiosError, 'Enhanced 2FA - Verify Setup');
      throw error;
    }
  },

  // Verify 2FA during login
  verify: async (request: Enhanced2FAVerifyRequest): Promise<Enhanced2FAVerifyResponse> => {
    try {
      const response = await api.post('/auth/2fa/verify', request);
      return response.data;
    } catch (error) {
      logApiError(error as AxiosError, 'Enhanced 2FA - Verify');
      throw error;
    }
  },

  // Disable 2FA
  disable: async (request: Disable2FARequest): Promise<{ message: string }> => {
    try {
      const response = await api.post('/auth/2fa/disable', request);
      return response.data;
    } catch (error) {
      logApiError(error as AxiosError, 'Enhanced 2FA - Disable');
      throw error;
    }
  },

  // Regenerate backup codes
  regenerateBackupCodes: async (request: RegenerateBackupCodesRequest): Promise<RegenerateBackupCodesResponse> => {
    try {
      const response = await api.post('/auth/2fa/backup-codes/regenerate', request);
      return response.data;
    } catch (error) {
      logApiError(error as AxiosError, 'Enhanced 2FA - Regenerate Backup Codes');
      throw error;
    }
  },

  // Get 2FA status
  getStatus: async (): Promise<User2FAStatusResponse> => {
    try {
      const response = await api.get('/auth/2fa/status');
      return response.data;
    } catch (error) {
      logApiError(error as AxiosError, 'Enhanced 2FA - Get Status');
      throw error;
    }
  }
};

// ============================================================================
// PASSKIT API
// ============================================================================

export const passkitAPI = {
  // List Programs (Internal API)
  listPrograms: async () => {
    try {
      const response = await api.get('/resources/programs');
      return response.data;
    } catch (error) {
      logApiError(error as AxiosError, 'Resources - List Programs');
      throw error;
    }
  },

  // List Campaigns (Internal API)
  listCampaigns: async () => {
    try {
      const response = await api.get('/resources/campaigns');
      return response.data;
    } catch (error) {
      logApiError(error as AxiosError, 'Resources - List Campaigns');
      throw error;
    }
  },

  // List Productions (Internal API)
  listProductions: async () => {
    try {
      const response = await api.get('/resources/productions');
      return response.data;
    } catch (error) {
      logApiError(error as AxiosError, 'Resources - List Productions');
      throw error;
    }
  },

  // Get Company Programs (Internal API)
  getCompanyPrograms: async () => {
    try {
      const response = await api.get('/membership/programs');
      return response.data;
    } catch (error) {
      logApiError(error as AxiosError, 'Resources - Get Company Programs');
      throw error;
    }
  },

  // Get Company Campaigns (Internal API)
  getCompanyCampaigns: async () => {
    try {
      const response = await api.get('/coupons/campaigns');
      return response.data;
    } catch (error) {
      logApiError(error as AxiosError, 'Resources - Get Company Campaigns');
      throw error;
    }
  },

  // Get Company Productions (Internal API)
  getCompanyProductions: async () => {
    try {
      const response = await api.get('/productions/');
      return response.data;
    } catch (error) {
      logApiError(error as AxiosError, 'Productions - Get Company');
      throw error;
    }
  },

  // Link Resource (generic endpoint function)
  linkResource: async (endpoint: string, payload: Record<string, unknown>) => {
    try {
      const response = await api.post(`/passkit/${endpoint}`, payload);
      return response.data;
    } catch (error) {
      logApiError(error as AxiosError, 'Resources - Link Resource');
      throw error;
    }
  },

  // Unlink Resource (generic endpoint function)
  unlinkResource: async (endpoint: string) => {
    try {
      const response = await api.delete(`/resources/${endpoint}`);
      return response.data;
    } catch (error) {
      logApiError(error as AxiosError, 'Resources - Unlink Resource');
      throw error;
    }
  },

  // ============================================================================
  // TICKET TYPE / EVENT MANAGEMENT API
  // ============================================================================

  // Get Ticket Type Templates for Production
  getTicketTypeTemplates: async (productionId: string) => {
    try {
      const response = await api.get(`/productions/${productionId}/ticket-type-templates`);
      return response.data;
    } catch (error) {
      logApiError(error as AxiosError, 'Ticket Types - Get Templates');
      throw error;
    }
  },

  // Get Ticket Type Template Fields
  getTicketTypeTemplateFields: async (ticketTypeId: string, productionId: string) => {
    try {
      const response = await api.get(`/productions/${productionId}/ticket-types/${ticketTypeId}/template-fields`);
      return response.data;
    } catch (error) {
      logApiError(error as AxiosError, 'Ticket Types - Get Template Fields');
      throw error;
    }
  },

  // Create Ticket Type
  createTicketType: async (productionId: string, data: {
    name: string;
    base_ticket_type_id: string;
    uid?: string;
    custom_fields?: Record<string, any>;
  }) => {
    try {
      const response = await api.post(`/resources/productions/${productionId}/ticket-types`, data);
      return response.data;
    } catch (error) {
      logApiError(error as AxiosError, 'Ticket Types - Create');
      throw error;
    }
  },

  // Update Ticket Type
  updateTicketType: async (productionId: string, ticketTypeId: string, data: {
    name?: string;
    uid?: string;
    ticketTypeConditions?: string;
    beforeRedeemPassTemplateId?: string;
    afterRedeemPassTemplateId?: string;
  }) => {
    try {
      const response = await api.patch(`/productions/${productionId}/ticket-types/${ticketTypeId}`, data);
      return response.data;
    } catch (error) {
      logApiError(error as AxiosError, 'Ticket Types - Update');
      throw error;
    }
  },

  // Delete Ticket Type
  deleteTicketType: async (productionId: string, ticketTypeId: string) => {
    try {
      const response = await api.delete(`/productions/${productionId}/ticket-types/${ticketTypeId}`);
      return response.data;
    } catch (error) {
      logApiError(error as AxiosError, 'Ticket Types - Delete');
      throw error;
    }
  },

  // Find Unused Templates (universal for all resource types)
  findUnusedTemplates: async (resourceId: string, resourceType: 'productions' | 'campaigns' | 'programs' = 'productions') => {
    try {

      let endpoint = '';
      switch (resourceType) {
        case 'productions':
          endpoint = `/productions/${resourceId}/templates/unused`;
          break;
        case 'campaigns':
          endpoint = `/coupons/campaigns/${resourceId}/templates/unused`;
          break;
        case 'programs':
          endpoint = `/membership/programs/${resourceId}/templates/unused`;
          break;
      }

      const response = await api.get(endpoint);
      return response.data;
    } catch (error) {
      logApiError(error as AxiosError, 'Templates - Find Unused');
      throw error;
    }
  },

  // Delete Template (universal for all resource types)
  deleteTemplate: async (resourceId: string, templateId: string, resourceType: 'productions' | 'campaigns' | 'programs' = 'productions') => {
    try {

      let endpoint = '';
      switch (resourceType) {
        case 'productions':
          endpoint = `/productions/${resourceId}/templates/${templateId}`;
          break;
        case 'campaigns':
          endpoint = `/coupons/campaigns/${resourceId}/templates/${templateId}`;
          break;
        case 'programs':
          endpoint = `/membership/programs/${resourceId}/templates/${templateId}`;
          break;
      }

      const response = await api.delete(endpoint);
      return response.data;
    } catch (error) {
      logApiError(error as AxiosError, 'Templates - Delete');
      throw error;
    }
  },

  // Create Ticket Type with Custom Images and Events (Combined Endpoint)
  createTicketTypeWithEvents: async (productionId: string, data: {
    ticket_type_name: string;
    base_template_id: string;
    images: {
      icon: string;
      logo: string;
      eventStrip: string;
      hero: string;
    };
    venue_id?: string;
    venue?: {
      name: string;
      address: string;
      timezone?: string;
    };
    event_dates: string[];
  }) => {
    try {
      const response = await api.post(`/productions/${productionId}/ticket-types-with-events`, data);
      return response.data;
    } catch (error) {
      logApiError(error as AxiosError, 'Ticket Types - Create with Events');
      throw error;
    }
  },

  // List all venues
  listVenues: async (search?: string) => {
    try {
      const params = search ? { search } : {};
      const response = await api.get('/productions/venues', { params });
      return response.data;
    } catch (error) {
      logApiError(error as AxiosError, 'Venues - List');
      throw error;
    }
  },

  // Add event to existing ticket type
  addEventToTicketType: async (productionId: string, ticketTypeId: string, data: {
    event_date: string;
    venue_id: string;
  }) => {
    try {
      const response = await api.post(`/productions/${productionId}/ticket-types/${ticketTypeId}/events`, data);
      return response.data;
    } catch (error) {
      logApiError(error as AxiosError, 'Events - Add to Ticket Type');
      throw error;
    }
  },

  // Create event for production (not tied to ticket type)
  createEvent: async (data: {
    productionId: string;
    scheduledStartDate: string;
    venueId: string;
  }) => {
    try {
      const response = await api.post('/productions/events', {
        productionId: data.productionId,
        scheduledStartDate: data.scheduledStartDate,
        venue: { id: data.venueId }
      });
      return response.data;
    } catch (error) {
      logApiError(error as AxiosError, 'Events - Create');
      throw error;
    }
  },

  updateEvent: async (eventId: string, data: {
    productionId?: string;
    scheduledStartDate?: string;
    venueId?: string;
  }) => {
    try {
      const payload: Record<string, unknown> = {};

      // Include productionId - required by PassKit
      if (data.productionId) {
        payload.productionId = data.productionId;
      }

      if (data.scheduledStartDate) {
        payload.scheduledStartDate = data.scheduledStartDate;
      }

      if (data.venueId) {
        payload.venue = { id: data.venueId };
      }

      const response = await api.patch(`/productions/events/${eventId}`, payload);
      return response.data;
    } catch (error) {
      logApiError(error as AxiosError, 'Events - Update');
      throw error;
    }
  },

  deleteEvent: async (eventId: string, data: {
    productionId: string;
    venueId: string;
  }) => {
    try {
      await api.delete(`/productions/events/${eventId}`, {
        data: data  // Send in request body
      });
    } catch (error) {
      logApiError(error as AxiosError, 'Events - Delete');
      throw error;
    }
  },

  // ============================================================================
  // TICKETS API
  // ============================================================================

  // List Tickets for Production
  listTickets: async (productionId: string) => {
    try {
      const response = await api.get(`/productions/${productionId}/tickets`);
      return response.data;
    } catch (error) {
      logApiError(error as AxiosError, 'Tickets - List');
      throw error;
    }
  },

  // Validate Ticket
  validateTicket: async (productionId: string, ticketId: string) => {
    try {
      const response = await api.post(`/productions/${productionId}/tickets/${ticketId}/validate`);
      return response.data;
    } catch (error) {
      logApiError(error as AxiosError, 'Tickets - Validate');
      throw error;
    }
  },

  // Delete Ticket
  deleteTicket: async (productionId: string, ticketId: string) => {
    try {
      const response = await api.delete(`/productions/${productionId}/tickets/${ticketId}`);
      return response.data;
    } catch (error) {
      logApiError(error as AxiosError, 'Tickets - Delete');
      throw error;
    }
  }
};

// Export API_BASE_URL for backward compatibility
export { API_BASE_URL } from '../config/api';

// ============================================================================
// MEMBERSHIP TIERS API
// ============================================================================

export const membershipTiersAPI = {
  getTiers: async (templateId: string) => {
    try {
      const response = await api.get(`/membership-mt/templates/${templateId}/tiers`);
      return response.data;
    } catch (error) {
      logApiError(error as AxiosError, 'Membership Tiers - Get');
      throw error;
    }
  }
};

// ============================================================================
// RE-EXPORTS FROM LEGACY API SERVICES (multi-tenant APIs migrated here)
// ============================================================================

export {
  multiTenantCouponAPI,
  multiTenantMembershipAPI,
  twoFactorAPI,
  performanceAPI,
} from './apiServices';
